import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RegistryCharacter } from '../../../types';

interface CharactersTabProps {
  characters: RegistryCharacter[];
}

function CharacterDetail({ char, t }: { char: RegistryCharacter; t: (key: string, opts?: Record<string, unknown>) => string }) {
  return (
    <>
      <h3 className="registry-detail-panel-name">{char.name}</h3>

      {char.aliases?.length > 0 && (
        <div className="registry-card-field">
          <span className="registry-field-label">{t('characters.aliases')}</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
            {char.aliases.map((a) => (
              <span key={a} className="registry-tag">{a}</span>
            ))}
          </div>
        </div>
      )}

      {char.bio && (
        <div className="registry-card-field">
          <span className="registry-field-label">{t('characters.bio')}</span>
          <p className="registry-field-text">{char.bio}</p>
        </div>
      )}

      {char.appearance && (
        <div className="registry-card-field">
          <span className="registry-field-label">{t('characters.appearance')}</span>
          <p className="registry-field-text">{char.appearance}</p>
        </div>
      )}

      {char.tags?.length > 0 && (
        <div className="registry-card-field">
          <span className="registry-field-label">{t('characters.tags')}</span>
          <div className="registry-tag-row">
            {char.tags.map((tag) => (
              <span key={tag} className="registry-tag registry-tag-info">{tag}</span>
            ))}
          </div>
        </div>
      )}

      {char.appeared_in_episodes?.length > 0 && (
        <div className="registry-card-field">
          <span className="registry-field-label">{t('characters.episodes')}</span>
          <div className="registry-episode-badges">
            {char.appeared_in_episodes.map((ep) => (
              <span key={ep} className="registry-ep-badge">{ep}</span>
            ))}
          </div>
        </div>
      )}

      {Object.keys(char.relationships ?? {}).length > 0 && (
        <div className="registry-card-field">
          <span className="registry-field-label">{t('characters.relationships')}</span>
          <div className="registry-kv-list">
            {Object.entries(char.relationships).map(([key, value]) => (
              <div key={key} className="registry-kv-row">
                <span className="registry-kv-key">{key}</span>
                <span className="registry-kv-value">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {char.state_timeline?.length > 0 && (
        <div className="registry-card-field">
          <span className="registry-field-label">{t('characters.stateTimeline')}</span>
          {char.state_timeline.map((st, si) => (
            <div key={si} className="registry-timeline-entry">
              <span className="registry-timeline-label">
                {t('characters.episodeStart', { ep: st.episode_start })}
              </span>
              <div className="registry-kv-list">
                {Object.entries(st.states).map(([k, v]) => (
                  <div key={k} className="registry-kv-row">
                    <span className="registry-kv-key">{k}</span>
                    <span className="registry-kv-value">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default function CharactersTab({ characters }: CharactersTabProps) {
  const { t } = useTranslation('scriptSummary');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const sorted = [...characters].sort(
    (a, b) => (b.appeared_in_episodes?.length ?? 0) - (a.appeared_in_episodes?.length ?? 0),
  );

  const selectedChar = selectedIndex !== null ? sorted[selectedIndex] : null;

  return (
    <div className="registry-char-layout">
      <div className="registry-detail-panel">
        <h4 className="registry-detail-panel-title">{t('characters.detailPanel')}</h4>
        {selectedChar ? (
          <CharacterDetail char={selectedChar} t={t} />
        ) : (
          <p className="registry-detail-panel-hint">{t('characters.selectCharHint')}</p>
        )}
      </div>

      <div className="registry-char-grid">
        <div className="registry-card-grid">
          {sorted.map((char, i) => (
            <div
              key={`${char.name}-${i}`}
              className={`registry-card ${selectedIndex === i ? 'selected' : ''}`}
              onClick={() => setSelectedIndex(i)}
            >
              <div className="registry-card-header">
                <h4 className="registry-card-title">{char.name}</h4>
              </div>

              {char.aliases?.length > 0 && (
                <div className="registry-card-field">
                  <span className="registry-field-label">{t('characters.aliases')}</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                    {char.aliases.map((a) => (
                      <span key={a} className="registry-tag">{a}</span>
                    ))}
                  </div>
                </div>
              )}

              {char.bio && (
                <div className="registry-card-field">
                  <span className="registry-field-label">{t('characters.bio')}</span>
                  <p className="registry-field-text">{char.bio}</p>
                </div>
              )}

              {char.appearance && (
                <div className="registry-card-field">
                  <span className="registry-field-label">{t('characters.appearance')}</span>
                  <p className="registry-field-text">{char.appearance}</p>
                </div>
              )}

              {char.tags?.length > 0 && (
                <div className="registry-card-field">
                  <span className="registry-field-label">{t('characters.tags')}</span>
                  <div className="registry-tag-row">
                    {char.tags.map((tag) => (
                      <span key={tag} className="registry-tag registry-tag-info">{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              {char.appeared_in_episodes?.length > 0 && (
                <div className="registry-card-field">
                  <span className="registry-field-label">{t('characters.episodes')}</span>
                  <div className="registry-episode-badges">
                    {char.appeared_in_episodes.slice(0, 10).map((ep) => (
                      <span key={ep} className="registry-ep-badge">{ep}</span>
                    ))}
                    {char.appeared_in_episodes.length > 10 && (
                      <span className="registry-ep-badge registry-ep-overflow">+{char.appeared_in_episodes.length - 10}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
